import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import api from "../api/axiosInstance";
import { useAuth } from "./AuthProvider";
import { useProfileContext } from "./ProfileProvider";

const CandidateContext = createContext(null);

const defaultAdminEmails = [
  "jonas@nativecoders.se",
  "adiela.abishua@nativecoders.se",
  "wambui.mwangi@nativecoders.se",
  "jonas.linder@nativecoders.se",
];

const adminEmails = process.env.REACT_APP_KEYCLOAK_ADMIN_EMAILS
  ? defaultAdminEmails.concat(process.env.REACT_APP_KEYCLOAK_ADMIN_EMAILS.split(",").map((e) => e.trim()))
  : defaultAdminEmails;

export const CandidateProvider = ({ children }) => {
  const [candidate, setCandidate] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const keycloak = useAuth();
  const { loading: profileLoading } = useProfileContext();
  const email = keycloak?.tokenParsed?.email || keycloak?.email;
  // Admin if the email is allow-listed (bootstrap admins) OR the Keycloak token
  // carries the 'admin' realm role. The realm role may be missing on a freshly
  // issued token (it's assigned server-side on first login), so the email
  // allowlist is the reliable fallback.
  const isAdmin =
    (email ? adminEmails.includes(email) : false) ||
    (keycloak?.hasRealmRole?.("admin") ?? false);

  const fetchCandidate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/candidates/me');
      setCandidate(response.data);
    } catch (error) {
      console.error("Failed to fetch candidate profile:", error);
      // If 404, candidate doesn't exist yet - that's okay
      if (error.response?.status === 404) {
        setCandidate({});
      } else {
        setError(error.response?.data?.error || "Failed to fetch candidate profile");
        setCandidate({});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCandidate = useCallback(async () => {
    try {
      const response = await api.get('/candidates/me');
      setCandidate(response.data);
      setError(null);
    } catch (error) {
      console.error("Failed to refresh candidate profile:", error);
      if (error.response?.status === 404) {
        setCandidate({});
      } else {
        setError(error.response?.data?.error || "Failed to refresh candidate profile");
      }
    }
  }, []);

  const createCandidate = useCallback(async (candidateData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/candidates/me', candidateData);
      setCandidate(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to create candidate profile:", error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to create candidate profile";
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCandidate = useCallback(async (candidateData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.patch('/candidates/me', candidateData);
      setCandidate(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to update candidate profile:", error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to update candidate profile";
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveCandidate = useCallback(async (candidateData) => {
    // If candidate exists, update; otherwise create
    if (candidate) {
      return await updateCandidate(candidateData);
    } else {
      return await createCandidate(candidateData);
    }
  }, [candidate, updateCandidate, createCandidate]);

  useEffect(() => {
    if (!keycloak || !keycloak.authenticated) {
      return;
    }

    if (profileLoading) {
      return;
    }

    if (isAdmin) {
      setCandidate({});
      setLoading(false);
      setError(null);
      return;
    }

    fetchCandidate();
  }, [keycloak, keycloak?.authenticated, profileLoading, isAdmin, fetchCandidate]);

  return (
    <CandidateContext.Provider
      value={{
        candidate,
        loading,
        error,
        isAdmin,
        refreshCandidate,
        createCandidate,
        updateCandidate,
        saveCandidate
      }}
    >
      {children}
    </CandidateContext.Provider>
  );
};

export const useCandidate = () => {
  const context = useContext(CandidateContext);
  return context?.candidate;
};

export const useCandidateContext = () => {
  const context = useContext(CandidateContext);
  if (!context) {
    throw new Error('useCandidateContext must be used within CandidateProvider');
  }
  return context;
};

